import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Input, Button } from "@nextui-org/react";
import { useTranslation } from "react-i18next";

import DefaultLayout from "@/layouts/default";
import { inventoryAPI, booksAPI } from "@/services/api";

interface InventoryItem {
  id: string;
  bookId: string;
  bookName: string;
  quantity: number;
  price: number; // Purchase price per item
  pricePerBook: number;
  totalCost: number;
  date?: string;
  supplierName?: string;
}

const InventoryEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [inventory, setInventory] = useState<InventoryItem>({
    id: "",
    bookId: "",
    bookName: "",
    quantity: 0,
    price: 0,
    pricePerBook: 0,
    totalCost: 0,
  });

  useEffect(() => {
    const fetchInventory = async () => {
      if (!id) return;
      try {
        setLoading(true);
        setError(null);

        console.log("Fetching inventory item with ID:", id);
        const inventoryData = await inventoryAPI.getById(id);

        console.log("Received inventory data:", inventoryData);

        // Get price and quantity values
        const quantity = Number(inventoryData.quantity) || 0;
        const pricePerBook = Number(inventoryData.price_per_book) || Number(inventoryData.pricePerBook) || 0;
        const price = Number(inventoryData.price) || 0;
        
        // Calculate total cost if not provided
        const totalCost = Number(inventoryData.total_cost) || 
                         Number(inventoryData.totalCost) || 
                         (quantity * pricePerBook);

        // Ensure all required fields are present and correctly typed
        const processedData = {
          id: inventoryData.id,
          bookId: inventoryData.book_id || inventoryData.bookId || "",
          bookName: inventoryData.book_name || inventoryData.bookName || "",
          quantity: quantity,
          price: price,
          pricePerBook: pricePerBook,
          totalCost: totalCost,
          date: inventoryData.purchase_date || 
                inventoryData.date ||
                inventoryData.created_at ||
                inventoryData.createdAt,
          supplierName: inventoryData.supplier_name || inventoryData.supplierName || "N/A",
        };

        console.log("Processed inventory data for edit:", processedData);

        setInventory(processedData);
        setLoading(false);
      } catch (error: any) {
        console.error("Error fetching inventory:", error);
        setError(error.message || "Failed to fetch inventory item");
        setLoading(false);
      }
    };

    fetchInventory();
  }, [id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numericValue = parseFloat(value);

    // Calculate total cost based on current state and input change
    const updatedInventory = {
      ...inventory,
      [name]:
        name === "quantity" || name === "pricePerBook" || name === "price" ? numericValue : value,
    };

    // Recalculate totalCost based on updated values
    if (name === "quantity" || name === "pricePerBook") {
      updatedInventory.totalCost =
        updatedInventory.quantity * updatedInventory.pricePerBook;
    }

    setInventory(updatedInventory);
  };

  const handleSubmit = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      console.log("Updating inventory item:", inventory);

      // Get current inventory data to calculate stock difference
      const currentInventory = await inventoryAPI.getById(id);
      const oldQuantity = Number(currentInventory.quantity) || 0;

      // Get the book associated with this inventory item
      const bookId = inventory.bookId || currentInventory.book_id;
      if (!bookId) {
        throw new Error("Book ID not found");
      }
      
      const book = await booksAPI.getById(bookId);
      console.log("Associated book data:", book);

      // Calculate new stock value
      const currentStock = Number(book.stock) || 0;
      const newStock = currentStock - oldQuantity + inventory.quantity;

      console.log("Stock calculation:", {
        currentStock,
        oldQuantity,
        newQuantity: inventory.quantity,
        newStock,
      });

      // Update the book stock and price
      await booksAPI.update(bookId, {
        ...book,
        stock: newStock,
        price: inventory.pricePerBook,
        // Ensure we're using the right field names for the backend
        pricePerBook: inventory.pricePerBook, 
      });

      // Prepare data for the API call
      const apiData = {
        ...inventory,
        book_id: inventory.bookId,
        book_name: inventory.bookName,
        price_per_book: inventory.pricePerBook,
        price: inventory.price,
        quantity: inventory.quantity,
        // Also include camelCase versions for flexibility
        bookId: inventory.bookId,
        bookName: inventory.bookName,
        pricePerBook: inventory.pricePerBook,
        totalCost: inventory.totalCost,
      };

      console.log("Sending data to API:", apiData);

      // Update the inventory item
      await inventoryAPI.update(id, apiData);

      setLoading(false);
      alert(t("Inventory updated successfully!"));
      navigate("/inventory");
    } catch (error: any) {
      console.error("Error updating inventory:", error);
      setError(error.message || "Failed to update inventory");
      setLoading(false);
      alert(t("Failed to update inventory: ") + (error.message || "Unknown error"));
    }
  };

  if (loading) {
    return (
      <DefaultLayout>
        <div className="flex justify-center items-center h-64">
          <p>{t("Loading...")}</p>
        </div>
      </DefaultLayout>
    );
  }

  if (error) {
    return (
      <DefaultLayout>
        <div className="max-w-lg mx-auto p-4">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
            {error}
          </div>
          <Button color="primary" onClick={() => navigate("/inventory")}>
            {t("Back to Inventory")}
          </Button>
        </div>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout>
      <section className="flex flex-col items-center justify-center py-8">
        <div className="max-w-lg w-full p-6 rounded-lg shadow-lg border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <h2 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-4">
            {t("Edit Inventory")}
          </h2>
          <div className="grid grid-cols-2 gap-x-8 gap-y-4">
            <div className="flex flex-col p-2">
              <label
                className="font-semibold text-gray-700 dark:text-gray-300"
                htmlFor="bookName"
              >
                {t("Book Name")}
              </label>
              <Input
                fullWidth
                isReadOnly
                id="bookName"
                name="bookName"
                type="text"
                value={inventory.bookName || ""}
                onChange={handleInputChange}
              />
            </div>
            <div className="flex flex-col p-2">
              <label
                className="font-semibold text-gray-700 dark:text-gray-300"
                htmlFor="quantity"
              >
                {t("Quantity")}
              </label>
              <Input
                fullWidth
                disabled={loading}
                id="quantity"
                min="0"
                name="quantity"
                type="number"
                value={inventory.quantity.toString()}
                onChange={handleInputChange}
              />
            </div>
            <div className="flex flex-col p-2">
              <label
                className="font-semibold text-gray-700 dark:text-gray-300"
                htmlFor="pricePerBook"
              >
                {t("Price Per Book")}
              </label>
              <Input
                fullWidth
                disabled={loading}
                id="pricePerBook"
                min="0"
                name="pricePerBook"
                type="number"
                value={inventory.pricePerBook.toString()}
                step="0.01"
                onChange={handleInputChange}
              />
            </div>
            <div className="flex flex-col p-2">
              <label
                className="font-semibold text-gray-700 dark:text-gray-300"
                htmlFor="price"
              >
                {t("Price")}
              </label>
              <Input
                fullWidth
                disabled={loading}
                id="price"
                min="0"
                name="price"
                type="number"
                value={inventory.price.toString()}
                step="0.01"
                onChange={handleInputChange}
              />
            </div>
            <div className="flex flex-col p-2">
              <label
                className="font-semibold text-gray-700 dark:text-gray-300"
                htmlFor="totalCost"
              >
                {t("Total Cost")}
              </label>
              <Input
                fullWidth
                isReadOnly
                id="totalCost"
                name="totalCost"
                type="text"
                value={`${inventory.totalCost.toLocaleString('en-IN', {
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2
                })} Rs`}
              />
            </div>
          </div>
          <div className="flex justify-center gap-4 mt-6">
            <Button color="primary" disabled={loading} onClick={handleSubmit}>
              {loading ? t("Saving...") : t("Save Changes")}
            </Button>
            <Button
              color="default"
              disabled={loading}
              onClick={() => navigate("/inventory")}
            >
              {t("Back")}
            </Button>
          </div>
        </div>
      </section>
    </DefaultLayout>
  );
};

export default InventoryEdit;
